import React, { useState, useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Helmet } from "react-helmet"
import NavigationBar from "../../components/ui/NavigationBar"
import Icon from "../../components/AppIcon"
import Button from "../../components/ui/Button"
import { authHelpers } from "../../firebaseClient"
import {
  subscribeLiveClass,
  joinLiveClass,
  leaveLiveClass,
  subscribeSelfAttendeeStatus,
  subscribeChatMessages,
  sendChatMessage,
  sendReaction,
  subscribeActivePoll,
  submitPollVote,
  subscribePollVotes,
  subscribeActiveLiveQuiz,
  submitQuizAnswer,
  subscribeQuizAnswers,
  subscribeWhiteboardOpen,
  subscribeAttendees,
} from "../../utils/liveClassHelpers"
import { createAgoraClient, joinChannel, leaveChannel } from "../../utils/agoraHelpers"
import LiveWhiteboard from "../../components/LiveWhiteboard"

const REACTION_EMOJIS = ["👍", "❤️", "🎉", "🙌"]

const LiveClassroomViewer = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orgId = searchParams?.get("orgId")
  const classId = searchParams?.get("classId")

  const [currentUser, setCurrentUser] = useState(null)
  const [liveClass, setLiveClass] = useState(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [banned, setBanned] = useState(false)
  const [kicked, setKicked] = useState(false)
  const [joinError, setJoinError] = useState("")
  const [hasVideo, setHasVideo] = useState(false)
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState("")
  const [activePoll, setActivePoll] = useState(null)
  const [pollVotes, setPollVotes] = useState([])
  const [activeQuiz, setActiveQuiz] = useState(null)
  const [quizAnswers, setQuizAnswers] = useState([])
  const [whiteboardOpen, setWhiteboardOpen] = useState(false)
  const [attendees, setAttendees] = useState([])
  const [showParticipants, setShowParticipants] = useState(false)
  const [agoraReady, setAgoraReady] = useState(false)
  const clientRef = useRef(null)
  const videoContainerRef = useRef(null)
  const hasJoinedFirestoreRef = useRef(false)
  const agoraJoinedRef = useRef(false)

  // ── auth + load the class (real-time, so status flips from
  // scheduled → live → ended without a refresh) ──
  useEffect(() => {
    let unsubscribeClass = () => {}

    const init = async () => {
      const { session } = await authHelpers.getSession()
      if (!session) {
        navigate("/authentication-portal", { replace: true })
        return
      }
      setCurrentUser(session.user)

      if (!orgId || !classId) {
        setNotFound(true)
        setLoading(false)
        return
      }

      unsubscribeClass = subscribeLiveClass(orgId, classId, (data) => {
        if (!data) setNotFound(true)
        else setLiveClass(data)
        setLoading(false)
      })
    }
    init()

    return () => unsubscribeClass()
  }, [orgId, classId, navigate])

  // ── once the class is live, join Agora as audience + record attendance ──
  useEffect(() => {
    if (!currentUser || liveClass?.status !== "live" || agoraJoinedRef.current) return
    agoraJoinedRef.current = true
    let cancelled = false

    const joinAsViewer = async () => {
      try {
        await joinLiveClass(
          orgId, classId, currentUser.uid,
          currentUser.displayName || currentUser.email || "Learner",
          currentUser.photoURL
        )
        hasJoinedFirestoreRef.current = true
        if (cancelled) return

        const client = createAgoraClient()
        clientRef.current = client

        client.on("user-published", async (user, mediaType) => {
          await client.subscribe(user, mediaType)
          if (mediaType === "video") {
            setHasVideo(true)
            if (videoContainerRef.current) user.videoTrack.play(videoContainerRef.current)
          }
          if (mediaType === "audio") {
            user.audioTrack.play()
          }
        })

        client.on("user-unpublished", (user, mediaType) => {
          if (mediaType === "video") setHasVideo(false)
        })

        await joinChannel(client, classId, currentUser.uid, "audience")
        setAgoraReady(true)
      } catch (e) {
        if (e?.message === "BANNED") {
          setBanned(true)
        } else {
          console.error("[LiveClassroomViewer] failed to join:", e)
          setJoinError("Couldn't connect to the live session. Please try again.")
        }
        agoraJoinedRef.current = false
      }
    }

    joinAsViewer()
    return () => { cancelled = true }
  }, [currentUser, liveClass?.status, orgId, classId])

  // ── detect a kick/ban: our attendee doc disappearing after we'd
  // successfully joined it means the host removed us ──
  useEffect(() => {
    if (!currentUser || !orgId || !classId) return
    const unsubscribe = subscribeSelfAttendeeStatus(orgId, classId, currentUser.uid, (stillPresent) => {
      if (!stillPresent && hasJoinedFirestoreRef.current) {
        setKicked(true)
        leaveChannel(clientRef.current, null).catch(() => {})
        agoraJoinedRef.current = false
      }
    })
    return unsubscribe
  }, [currentUser, orgId, classId])

    // ── chat ──
  useEffect(() => {
    if (!orgId || !classId) return
    return subscribeChatMessages(orgId, classId, setChatMessages)
  }, [orgId, classId])

    // ── active poll + live tally ──
  useEffect(() => {
    if (!orgId || !classId) return
    return subscribeActivePoll(orgId, classId, setActivePoll)
  }, [orgId, classId])

  useEffect(() => {
    if (!orgId || !classId || !activePoll?.id) { setPollVotes([]); return }
    return subscribePollVotes(orgId, classId, activePoll.id, setPollVotes)
  }, [orgId, classId, activePoll?.id])

  // ── active quiz question + live answers ──
  useEffect(() => {
    if (!orgId || !classId) return
    return subscribeActiveLiveQuiz(orgId, classId, setActiveQuiz)
  }, [orgId, classId])

    useEffect(() => {
    if (!orgId || !classId || !activeQuiz?.id) { setQuizAnswers([]); return }
    return subscribeQuizAnswers(orgId, classId, activeQuiz.id, setQuizAnswers)
  }, [orgId, classId, activeQuiz?.id])

    useEffect(() => {
    if (!orgId || !classId) return
    return subscribeWhiteboardOpen(orgId, classId, setWhiteboardOpen)
  }, [orgId, classId])

  useEffect(() => {
    if (!orgId || !classId) return
    return subscribeAttendees(orgId, classId, setAttendees)
  }, [orgId, classId])

  // Same real-count approach as the host screen — derived from the actual
  // attendees subcollection, not the class doc's attendeeCount field
  // (which drifted upward on every refresh; see liveClassHelpers.js).
  const activeAttendees = attendees?.filter((a) => !a?.leftAtMs) || []

  // ── clean up on unmount ──
  useEffect(() => {
    return () => {
      leaveChannel(clientRef.current, null).catch(() => {})
      if (hasJoinedFirestoreRef.current && currentUser && orgId && classId) {
        leaveLiveClass(orgId, classId, currentUser.uid).catch(() => {})
      }
    }
  }, [currentUser, orgId, classId])

  const handleSendChat = async () => {
    if (!chatInput?.trim() || !currentUser) return
    await sendChatMessage(orgId, classId, currentUser.uid, currentUser.displayName || "Learner", currentUser.photoURL, chatInput)
    setChatInput("")
  }

    const handleReact = (emoji) => {
    if (!currentUser) return
    sendReaction(orgId, classId, currentUser.uid, emoji).catch(() => {})
  }

    const myVote = pollVotes?.find((v) => v?.uid === currentUser?.uid)

  const handleVote = async (optionId) => {
    if (!currentUser || !activePoll?.id) return
    await submitPollVote(orgId, classId, activePoll.id, currentUser.uid, optionId)
  }

  const myQuizAnswer = quizAnswers?.find((a) => a?.uid === currentUser?.uid)

  const handleQuizAnswer = async (optionId) => {
    if (!currentUser || !activeQuiz?.id) return
    await submitQuizAnswer(orgId, classId, activeQuiz.id, currentUser.uid, optionId, activeQuiz?.correctOptionId)
  }

  const handleLeave = async () => {
    await leaveChannel(clientRef.current, null)
    if (hasJoinedFirestoreRef.current && currentUser) {
      await leaveLiveClass(orgId, classId, currentUser.uid).catch(() => {})
    }
    if (liveClass?.courseId) navigate(`/video-learning-interface?orgId=${orgId}&courseId=${liveClass.courseId}`)
    else navigate("/user-dashboard")
  }

  const formatSchedule = (ms) => {
    if (!ms) return null
    return new Date(ms).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Joining classroom...</span>
        </div>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <Icon name="SearchX" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Live class not found</h1>
          <p className="text-muted-foreground mb-6">This session may have been removed.</p>
          <Button variant="default" onClick={() => navigate("/user-dashboard")}>Back to Dashboard</Button>
        </div>
      </div>
    )
  }

  if (banned) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <Icon name="ShieldAlert" size={40} className="text-destructive mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">You can't join this session</h1>
          <p className="text-muted-foreground mb-6">The host has restricted your access to this live class.</p>
          <Button variant="outline" onClick={() => navigate("/user-dashboard")}>Back to Dashboard</Button>
        </div>
      </div>
    )
  }

  if (kicked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center">
          <Icon name="LogOut" size={40} className="text-muted-foreground mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">You were removed from this class</h1>
          <p className="text-muted-foreground mb-6">The host removed you from the session.</p>
          <Button variant="outline" onClick={() => navigate("/user-dashboard")}>Back to Dashboard</Button>
        </div>
      </div>
    )
  }

  if (liveClass?.status === "scheduled") {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="max-w-lg mx-auto px-4 pt-28 sm:pt-32 pb-12 text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Clock" size={26} className="text-muted-foreground" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">{liveClass?.title}</h1>
          {liveClass?.scheduledAtMs && (
            <p className="text-muted-foreground mb-6 flex items-center justify-center gap-1.5">
              <Icon name="Calendar" size={14} /> {formatSchedule(liveClass?.scheduledAtMs)}
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            This page will update automatically once your instructor starts the session — no need to refresh.
          </p>
        </div>
      </div>
    )
  }

  if (liveClass?.status === "ended") {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="max-w-lg mx-auto px-4 pt-28 sm:pt-32 pb-12 text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Video" size={26} className="text-muted-foreground" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">{liveClass?.title}</h1>
          <p className="text-muted-foreground mb-6">This live session has ended.</p>
          <Button
            variant="default"
            onClick={() =>
              liveClass?.courseId
                ? navigate(`/video-learning-interface?orgId=${orgId}&courseId=${liveClass.courseId}`)
                : navigate("/user-dashboard")
            }
          >
            Back to Course
          </Button>
        </div>
      </div>
    )
  }

  // status === "live"
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Helmet><title>{liveClass?.title} — Live — Nesolearn</title></Helmet>
      <NavigationBar />

      <div className="flex-1 flex flex-col lg:flex-row max-w-7xl mx-auto w-full px-4 pb-4 pt-24 sm:pt-28 gap-4">
        <div className="flex-1 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1.5 bg-destructive text-white text-xs font-bold px-2 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> LIVE
              </span>
              <h1 className="font-semibold text-foreground">{liveClass?.title}</h1>
            </div>
                        <button
              onClick={() => setShowParticipants((s) => !s)}
              className="text-sm text-muted-foreground flex items-center gap-1.5 hover:text-foreground transition-smooth"
            >
              <Icon name="Users" size={14} /> {activeAttendees?.length || 0}
            </button>
          </div>

          <div className="bg-black rounded-xl aspect-video w-full relative overflow-hidden">
            <div ref={videoContainerRef} className="absolute inset-0" />
            {!hasVideo && (
              <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
                {joinError || "Waiting for the host's video..."}
              </div>
            )}
          </div>

              {whiteboardOpen && (
            <LiveWhiteboard client={agoraReady ? clientRef.current : null} isHost={false} />
          )}

          <div className="flex items-center justify-center gap-2">
            {REACTION_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => handleReact(emoji)}
                className="text-xl px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 transition-smooth"
              >
                {emoji}
              </button>
            ))}
            <Button variant="destructive" iconName="LogOut" iconPosition="left" onClick={handleLeave}>
              Leave
            </Button>
          </div>
        </div>

                <div className="w-full lg:w-80 flex flex-col gap-3">
          {activePoll && (
            <div className="bg-card border border-border rounded-xl p-3 space-y-2">
              <p className="text-sm font-medium text-foreground">{activePoll?.question}</p>
              {activePoll?.status === "open" && !myVote ? (
                <div className="space-y-1.5">
                  {activePoll?.options?.map((opt) => (
                    <button
                      key={opt?.id}
                      onClick={() => handleVote(opt?.id)}
                      className="w-full text-left text-sm px-3 py-1.5 rounded-md border border-input hover:bg-muted transition-smooth"
                    >
                      {opt?.text}
                    </button>
                  ))}
                </div>
              ) : (
                activePoll?.options?.map((opt) => {
                  const count = pollVotes?.filter((v) => v?.optionId === opt?.id)?.length || 0
                  const total = pollVotes?.length || 0
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0
                  const isMine = myVote?.optionId === opt?.id
                  return (
                    <div key={opt?.id} className="space-y-0.5">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{opt?.text}{isMine ? " ✓" : ""}</span>
                        <span>{pct}%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          )}

                    {activeQuiz && (
            <div className="bg-card border border-border rounded-xl p-3 space-y-2">
              <p className="text-sm font-medium text-foreground">{activeQuiz?.question}</p>
              {activeQuiz?.status === "open" && !myQuizAnswer ? (
                <div className="space-y-1.5">
                  {activeQuiz?.options?.map((opt) => (
                    <button
                      key={opt?.id}
                      onClick={() => handleQuizAnswer(opt?.id)}
                      className="w-full text-left text-sm px-3 py-1.5 rounded-md border border-input hover:bg-muted transition-smooth"
                    >
                      {opt?.text}
                    </button>
                  ))}
                </div>
              ) : activeQuiz?.status === "open" ? (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Icon name="Clock" size={13} /> Answer submitted — waiting for the host to reveal the answer.
                </p>
              ) : (
                <>
                  {myQuizAnswer && (
                    <p className={`text-sm flex items-center gap-1.5 ${myQuizAnswer?.correct ? "text-success" : "text-destructive"}`}>
                      <Icon name={myQuizAnswer?.correct ? "CheckCircle2" : "XCircle"} size={15} />
                      {myQuizAnswer?.correct ? "You got it right!" : "Not quite"}
                    </p>
                  )}
                  {activeQuiz?.options?.map((opt) => {
                    const count = quizAnswers?.filter((a) => a?.optionId === opt?.id)?.length || 0
                    const total = quizAnswers?.length || 0
                    const pct = total > 0 ? Math.round((count / total) * 100) : 0
                    const isCorrect = opt?.id === activeQuiz?.correctOptionId
                    return (
                      <div key={opt?.id} className="space-y-0.5">
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span className={isCorrect ? "text-success font-medium" : ""}>{opt?.text}{isCorrect ? " ✓" : ""}</span>
                          <span>{pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div className={`h-full ${isCorrect ? "bg-success" : "bg-accent"}`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          )}

                    <div className="flex-1 flex flex-col bg-card border border-border rounded-xl overflow-hidden">
          {showParticipants ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              <h3 className="text-sm font-semibold text-foreground px-1">Participants ({activeAttendees?.length || 0})</h3>
              {activeAttendees?.map((a) => (
                <div key={a?.uid} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted">
                  <span className="text-sm text-foreground">{a?.name}</span>
                </div>
              ))}
            </div>
          ) : (
          <>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {chatMessages?.length === 0 && (
              <p className="text-xs text-muted-foreground text-center pt-4">No messages yet — say hi 👋</p>
            )}
            {chatMessages?.map((m) => (
              <div key={m?.id} className="text-sm">
                <span className="font-medium text-foreground">{m?.name}: </span>
                <span className="text-muted-foreground">{m?.text}</span>
              </div>
            ))}
          </div>
          <div className="p-3 border-t border-border flex gap-2">
            <input
              value={chatInput}
              onChange={(e) => setChatInput(e?.target?.value)}
              onKeyDown={(e) => e?.key === "Enter" && handleSendChat()}
              placeholder="Message..."
              className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
                                                <Button variant="default" size="icon" iconName="Send" iconSize={14} onClick={handleSendChat} />
          </div>
          </>
          )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LiveClassroomViewer